import { useMemo, useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  EMERGENCY_TYPES,
  UNIT_COLOR,
  UNIT_SHORT,
  UNIT_TYPES,
  PATIENT_ASSESS_SECONDS,
  distanceKm,
  quickFinishGems,
} from "@/lib/gameConfig";
import { Diamond, X, Heartbeat, Star } from "@phosphor-icons/react";
import { getRoute } from "@/lib/routing";

const ROUTED_TOP_N = 5;

// Rough ETA in game seconds based on straight-line + driving speed +15 mph
const estimateEtaSec = (origin, call) => {
  const km = distanceKm(origin, call);
  // assume 40 mph average effective in town = 64 km/h, scaled by TRAVEL_TIME_SCALE 0.15
  const realSec = (km / 64) * 3600;
  return Math.max(4, realSec * 0.15);
};

const formatEta = (s) => {
  s = Math.round(s);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
};

export default function CallDialog({
  open,
  onOpenChange,
  call,
  stations,
  hospitals = [],
  apparatusState,
  dispatches,
  onDispatch,
  onCancelDispatch,
  onQuickFinish,
  onOpenTransport,
  onTransportArrestee,
  playerGems = 0,
  hasAlliance,
  isShared,
  onShareWithAlliance,
  followUpQueue = {},
  onToggleFollowUp,
}) {
  const [sortBy, setSortBy] = useState("eta"); // 'eta' | 'type'
  const [typeFilter, setTypeFilter] = useState("all");
  // "available" = units currently free (default). "followup" = units busy
  // on OTHER calls; user can queue them so they auto-dispatch here the
  // moment they finish their current job.
  const [listMode, setListMode] = useState("available"); // 'available' | 'followup'
  // Two-step confirm for Quick Finish (gem spend) — first click arms;
  // armed state auto-clears after 4 s if the player walks away.
  const [confirmQuickFinish, setConfirmQuickFinish] = useState(false);
  useEffect(() => {
    if (!confirmQuickFinish) return;
    const id = setTimeout(() => setConfirmQuickFinish(false), 4000);
    return () => clearTimeout(id);
  }, [confirmQuickFinish]);
  // Reset when the call changes / dialog closes so it doesn't carry over.
  useEffect(() => { setConfirmQuickFinish(false); }, [call?.id, open]);
  // Always default the units list back to "Available" whenever the dialog
  // opens OR the user navigates to a different call. Follow-Up mode is a
  // per-decision view — if the dispatcher closes the dialog we assume
  // they're done queueing and want to see the normal dispatch list next
  // time they open a call.
  useEffect(() => {
    setListMode("available");
  }, [call?.id, open]);
  // Routed ETAs from OSRM, keyed by apparatusId. Origin coords are kept so we
  // know when to refetch (e.g. a returning truck moved enough to matter).
  const [routedEtas, setRoutedEtas] = useState({});
  const [routingIds, setRoutingIds] = useState({}); // { [appId]: true } in-flight

  const t = call ? EMERGENCY_TYPES[call.type] : null;

  // Reset routed cache when call changes — different destination invalidates everything.
  useEffect(() => {
    setRoutedEtas({});
    setRoutingIds({});
  }, [call?.id]);

  // Counts for en-route / on-scene per type.
  //
  // Queued Follow-Up units (currently busy on another call, pre-assigned
  // to auto-dispatch here once free) are counted as EN ROUTE alongside
  // real en-route units. That way a queued Engine bumps the "+X ER" pill
  // and gives the dispatcher an accurate picture of incoming resources —
  // matching the user-visible behavior of "2/3 + 1 ER" once a follow-up
  // is queued to an unmet requirement.
  const counts = useMemo(() => {
    const onScene = {};
    const enRoute = {};
    UNIT_TYPES.forEach((u) => {
      onScene[u] = 0;
      enRoute[u] = 0;
    });
    if (!call) return { onScene, enRoute };
    dispatches.forEach((d) => {
      if (d.callId !== call.id || d.returning) return;
      const type = d.apparatusType || "Engine";
      if (d.onScene) onScene[type] = (onScene[type] || 0) + 1;
      else enRoute[type] = (enRoute[type] || 0) + 1;
    });
    const queuedIds = followUpQueue[String(call.id)] || [];
    queuedIds.forEach((aid) => {
      const type = apparatusState?.[aid]?.type || "Engine";
      enRoute[type] = (enRoute[type] || 0) + 1;
    });
    return { onScene, enRoute };
  }, [call, dispatches, followUpQueue, apparatusState]);

  // Stable sort tracking — locks row order against late-arriving OSRM data
  // and the 100 ms dispatch tick (returning units moving). Without this, the
  // list re-shuffles out from under the user's cursor mid-click and causes
  // accidental wrong-unit dispatches.
  //
  // We sort exclusively by each unit's STATION coords + straight-line ETA
  // (both stable across renders). Routed ETA and live returning-position
  // ETA are display-only; they don't influence row order. Sort order
  // changes only when the user picks a different sort mode / filter, or a
  // new unit becomes available / one leaves the list.

  // Available units across all stations + hospital bays (hospitals can house
  // Medic units which should be dispatchable just like any other apparatus).
  const available = useMemo(() => {
    if (!call) return [];
    // Map apparatusId -> live origin (current position if mid-return, else station coords).
    const returningByApp = {};
    dispatches.forEach((d) => {
      if (d.returning) returningByApp[d.apparatusId] = { lat: d.lat, lng: d.lng };
    });
    const out = [];
    const collectFrom = (s, sourceKind) => {
      (s.trucks || []).forEach((tr) => {
        if (apparatusState[tr.id]?.available) {
          const ret = returningByApp[tr.id];
          const origin = ret || { lat: s.lat, lng: s.lng };
          const straightEta = estimateEtaSec(origin, call);
          const routed = routedEtas[tr.id];
          const etaSec = routed ? routed.etaSec : straightEta;
          // Sort ETA is based on STATION coords only (never live returning
          // position) so the row order is stable across the 100 ms dispatch
          // tick. Display ETA still tracks live position via `etaSec` above.
          const sortEta = estimateEtaSec({ lat: s.lat, lng: s.lng }, call);
          out.push({
            id: tr.id,
            name: tr.name,
            type: tr.type || "Engine",
            stationId: sourceKind === "station" ? s.id : null,
            hospitalId: sourceKind === "hospital" ? s.id : null,
            stationName: s.name,
            stationKind: sourceKind, // "station" | "hospital" — used by the row label
            station: s,
            origin,
            etaSec,
            straightEtaSec: straightEta,
            sortEta,
            isRouted: !!routed,
            isRouting: !!routingIds[tr.id],
            returning: !!ret,
          });
        }
      });
    };
    stations.forEach((s) => collectFrom(s, "station"));
    (hospitals || []).forEach((h) => collectFrom(h, "hospital"));
    let filtered = out;
    if (typeFilter !== "all") filtered = filtered.filter((u) => u.type === typeFilter);
    // Stable sort: uses sortEta (station-based, doesn't change as units move)
    // and unit id as tiebreaker so the order is fully deterministic.
    filtered.sort((a, b) => {
      const primary = sortBy === "eta" ? a.sortEta - b.sortEta : a.type.localeCompare(b.type);
      return primary !== 0 ? primary : a.id.localeCompare(b.id);
    });
    return filtered;
  }, [call, stations, hospitals, apparatusState, dispatches, sortBy, typeFilter, routedEtas, routingIds]);

  // Follow-Up candidates — units currently busy on OTHER calls (not this
  // one). Displayed when the user toggles the list mode to "followup" so
  // they can pre-queue responders. Each candidate carries an ETA-until-
  // clear estimate + the home station name (to match the display shape of
  // `available` for reusable rendering). Filtered + sorted with the same
  // rules as the available list.
  const followUpCandidates = useMemo(() => {
    if (!call) return [];
    const now = Date.now();
    const stByApp = {};
    const hByApp = {};
    stations.forEach((s) => (s.trucks || []).forEach((t) => (stByApp[t.id] = s)));
    (hospitals || []).forEach((h) => (h.trucks || []).forEach((t) => (hByApp[t.id] = h)));
    const out = [];
    dispatches.forEach((d) => {
      // Only currently-busy units belonging to THE PLAYER (not helpers),
      // NOT on this call, not returning-home (returning = "will be
      // available shortly" but they're not "on scene of another call" per
      // the user's spec).
      if (d.callId === call?.id) return;
      if (d.returning) return;
      if (d.external) return; // helper units (mutual aid) — can't pre-queue those
      if (!d.callId) return; // no active call = must be returning; skip
      const st = stByApp[d.apparatusId];
      const h = hByApp[d.apparatusId];
      const home = st || h;
      if (!home) return;
      const type = d.apparatusType || "Engine";
      if (typeFilter !== "all" && type !== typeFilter) return;
      // Estimate seconds until this unit clears its current call. If
      // already on-scene, the call's own onSceneDurationSec + remaining
      // work timer is the best signal we have; otherwise fall back to
      // en-route ETA.
      let clearInSec = 0;
      if (d.onScene) {
        const otherCall = dispatches.find((x) => x.callId === d.callId && x.onScene);
        clearInSec = 60; // default fallback if we can't pinpoint the call's on-scene timer
        if (otherCall) {
          // Best-effort — we don't have the call object here.
          const started = otherCall.onSceneStartedAt || now;
          const dur = (otherCall.onSceneDurationSec || 60) * 1000;
          clearInSec = Math.max(5, Math.ceil((started + dur - now) / 1000));
        }
      } else {
        const elapsed = (now - (d.startedAt || now)) / 1000;
        clearInSec = Math.max(5, Math.ceil((d.durationSec || 60) - elapsed));
      }
      // Post-clear travel ETA to THIS call (straight line from unit's home
      // station — the unit will most likely be routed via station on its
      // way back before we re-dispatch, so station-origin is a reasonable
      // approximation and matches the sort logic used for `available`).
      const travelEta = estimateEtaSec({ lat: home.lat, lng: home.lng }, call);
      const totalEta = clearInSec + travelEta;
      out.push({
        id: d.apparatusId,
        name: d.apparatusName,
        type,
        stationId: st ? st.id : null,
        hospitalId: h ? h.id : null,
        stationName: home.name,
        stationKind: st ? "station" : "hospital",
        station: home,
        origin: { lat: home.lat, lng: home.lng },
        etaSec: totalEta,
        sortEta: totalEta,
        straightEtaSec: travelEta,
        clearInSec,
        travelEta,
        isRouted: false,
        isRouting: false,
        returning: false,
        // Is this unit already queued for THIS call?
        // NB: followUpQueue keys are STRINGS (object semantics); call.id
        // is a NUMBER from `nextCallId()`. Coerce both sides.
        queuedHere: (followUpQueue[String(call.id)] || []).includes(d.apparatusId),
        // Is it queued for a DIFFERENT call (locked out)?
        queuedElsewhere: Object.entries(followUpQueue).some(
          ([cid, ids]) => String(cid) !== String(call.id) && (ids || []).includes(d.apparatusId)
        ),
      });
    });
    // Sort by combined ETA (clear + travel) — matches "who will arrive here first".
    out.sort((a, b) => {
      const primary = sortBy === "eta" ? a.sortEta - b.sortEta : a.type.localeCompare(b.type);
      return primary !== 0 ? primary : a.id.localeCompare(b.id);
    });
    return out;
  }, [call, stations, hospitals, dispatches, typeFilter, sortBy, followUpQueue]);

  // Fetch real OSRM routed ETAs for the top N candidates (by current straight-line ETA).
  // Stable key prevents refetch every dispatch tick.
  const topNKey = useMemo(() => {
    if (!call) return "";
    // Re-derive top N strictly by straight-line eta, regardless of current sort/filter,
    // so the routed set is consistent across sort changes.
    const all = [];
    const returningByApp = {};
    dispatches.forEach((d) => {
      if (d.returning) returningByApp[d.apparatusId] = { lat: d.lat, lng: d.lng };
    });
    const addFrom = (s) => {
      (s.trucks || []).forEach((tr) => {
        if (apparatusState[tr.id]?.available) {
          const origin = returningByApp[tr.id] || { lat: s.lat, lng: s.lng };
          all.push({ id: tr.id, origin, sEta: estimateEtaSec(origin, call) });
        }
      });
    };
    stations.forEach(addFrom);
    (hospitals || []).forEach(addFrom);
    all.sort((a, b) => a.sEta - b.sEta);
    const top = all.slice(0, ROUTED_TOP_N);
    // Key encodes both call destination + each candidate's id + rounded origin.
    return top
      .map((u) => `${u.id}@${u.origin.lat.toFixed(2)},${u.origin.lng.toFixed(2)}`)
      .join("|");
  }, [call, stations, hospitals, apparatusState, dispatches]);

  const topNRef = useRef({ key: "", items: [] });
  useEffect(() => {
    if (!call || !topNKey) return;
    if (topNRef.current.key === topNKey) return;
    // Re-derive top N with origin info from the same logic above
    const returningByApp = {};
    dispatches.forEach((d) => {
      if (d.returning) returningByApp[d.apparatusId] = { lat: d.lat, lng: d.lng };
    });
    const all = [];
    const addFrom = (s) => {
      (s.trucks || []).forEach((tr) => {
        if (apparatusState[tr.id]?.available) {
          const origin = returningByApp[tr.id] || { lat: s.lat, lng: s.lng };
          all.push({ id: tr.id, type: tr.type, origin, sEta: estimateEtaSec(origin, call) });
        }
      });
    };
    stations.forEach(addFrom);
    (hospitals || []).forEach(addFrom);
    all.sort((a, b) => a.sEta - b.sEta);
    const top = all.slice(0, ROUTED_TOP_N);
    topNRef.current = { key: topNKey, items: top };

    // Mark in-flight for any not already routed
    const toFetch = top.filter((u) => !routedEtas[u.id]);
    if (toFetch.length === 0) return;
    setRoutingIds((prev) => {
      const next = { ...prev };
      toFetch.forEach((u) => {
        next[u.id] = true;
      });
      return next;
    });

    let cancelled = false;
    toFetch.forEach(async (u) => {
      try {
        const r = await getRoute(u.origin, { lat: call.lat, lng: call.lng }, { apparatusType: u.type });
        if (cancelled) return;
        setRoutedEtas((prev) => ({
          ...prev,
          [u.id]: {
            etaSec: r.durationSec,
            originLat: u.origin.lat,
            originLng: u.origin.lng,
            fallback: !!r.fallback,
          },
        }));
      } catch (_e) {
        // swallow; straight-line ETA remains as fallback
      } finally {
        if (!cancelled) {
          setRoutingIds((prev) => {
            const next = { ...prev };
            delete next[u.id];
            return next;
          });
        }
      }
    });
    return () => {
      cancelled = true;
    };
    // routedEtas intentionally NOT a dep — would cause infinite refetch loop.
    // We rely on topNKey to gate refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topNKey, call?.id, call?.lat, call?.lng]);

  if (!call || !t) return null;

  const required = call.requiredUnits || {};
  const requirementOptions = [required].concat(
    Array.isArray(call.alternates) ? call.alternates : []
  );
  const allMet = requirementOptions.some((opt) =>
    Object.entries(opt).every(([u, n]) => (counts.onScene[u] || 0) >= n)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none bg-[#0F1115] border-white/10 text-white max-w-2xl h-[85vh] max-h-[85vh] grid grid-rows-[auto_minmax(0,1fr)_auto] gap-0 p-4"
        data-testid="call-dialog"
      >
        <DialogHeader className="border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <span
              className="mono text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5"
              style={{ background: t.color, color: "#050505" }}
            >
              {t.short}
            </span>
            <DialogTitle className="mono uppercase tracking-widest text-xs text-zinc-300 font-semibold">
              {isShared && <span className="text-orange-300">🆘 ALLIANCE · </span>}
              {t.label}
            </DialogTitle>
            <span className="ml-auto mono text-[10px] uppercase text-emerald-400 mr-7" data-testid="call-reward">+${t.reward.toLocaleString()}</span>
          </div>
          <div className="mono text-[10px] text-zinc-400 uppercase tracking-wider mt-1 flex items-center gap-2">
            <span className="flex-1 truncate">{call.address}</span>
            {/* Quick Finish — sits to the LEFT of "Request Aid" so both
                emergency-bypass actions are grouped. Hidden once already
                quick-finished. */}
            {onQuickFinish && !call.quickFinish && (() => {
              const reward = EMERGENCY_TYPES[call.type]?.reward || 0;
              const cost = quickFinishGems(reward);
              const canAfford = playerGems >= cost;
              if (confirmQuickFinish) {
                return (
                  <span className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`quick-finish-confirm-${call.id}`}
                      onClick={() => { setConfirmQuickFinish(false); onQuickFinish(call.id); }}
                      title={`Spend ${cost} gems to instantly resolve this call`}
                      className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-red-500/60 text-red-300 bg-red-500/10 hover:bg-red-500/20 inline-flex items-center gap-1 transition-colors"
                    >
                      Confirm — <Diamond size={11} weight="fill" /> {cost}
                    </button>
                    <button
                      type="button"
                      data-testid={`quick-finish-cancel-${call.id}`}
                      onClick={() => setConfirmQuickFinish(false)}
                      title="Cancel"
                      className="border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 p-1"
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                );
              }
              return (
                <button
                  type="button"
                  data-testid={`quick-finish-${call.id}`}
                  onClick={() => setConfirmQuickFinish(true)}
                  disabled={!canAfford}
                  title={
                    canAfford
                      ? `Instantly resolve this call for ${cost} gems`
                      : `Need ${cost} gems (you have ${playerGems})`
                  }
                  className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border inline-flex items-center gap-1 transition-colors ${
                    canAfford
                      ? "border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10"
                      : "border-white/10 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  Finish — <Diamond size={11} weight="fill" /> {cost}
                </button>
              );
            })()}
            {hasAlliance && !isShared && (
              <button
                data-testid="share-with-alliance-btn"
                type="button"
                onClick={() => onShareWithAlliance && onShareWithAlliance(call.id)}
                className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-orange-500/60 text-orange-300 hover:bg-orange-500/10"
                title="Share this call with your Mutual Aid Group"
              >
                🆘 Request Aid
              </button>
            )}
            {isShared && (
              <span
                data-testid="shared-badge"
                className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-orange-500/60 text-orange-300 bg-orange-500/10"
              >
                🆘 SHARED
              </span>
            )}
          </div>
        </DialogHeader>

        {/* Middle region: top summary (required + patients + dispatched)
            scrolls independently, sort/filter bar is a fixed row, and the
            available/follow-up list gets the remaining vertical space so
            switching filters never resizes the dialog. */}
        <div className="flex flex-col min-h-0">
        <div className="overflow-y-auto pr-1 -mr-1 shrink-0 max-h-[50%]" data-testid="call-dialog-scroll">
        <div className="pt-2">
          {/* Requirements + on-scene status */}
          <div className="border border-white/10 px-2.5 py-1.5">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center justify-between">
              <span>Required Units</span>
              <span className="text-zinc-400">WORK {call.onSceneDurationSec}s</span>
            </div>
            {call.revealed ? (
              <div className="space-y-1" data-testid="required-units-revealed">
                {requirementOptions.map((opt, optIdx) => (
                  <div key={optIdx}>
                    {optIdx > 0 && (
                      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 my-1 flex items-center gap-2">
                        <span className="flex-1 h-px bg-white/10" />
                        <span>— OR —</span>
                        <span className="flex-1 h-px bg-white/10" />
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {Object.entries(opt).map(([type, n]) => {
                        const onS = counts.onScene[type] || 0;
                        const er = counts.enRoute[type] || 0;
                        const ok = onS >= n;
                        return (
                          <div key={`${optIdx}-${type}`} className="flex items-center gap-2 leading-tight" data-testid={`req-${type}`}>
                            <span
                              className="mono text-[9px] uppercase tracking-widest px-1 py-px"
                              style={{ background: UNIT_COLOR[type], color: "#050505" }}
                            >
                              {UNIT_SHORT[type]}
                            </span>
                            <span className="text-[11px] flex-1">{type}</span>
                            <span className={`mono text-xs ${ok ? "text-emerald-400" : "text-yellow-400"}`}>
                              {onS}/{n}
                            </span>
                            {er > 0 && (
                              <span className="mono text-[9px] text-zinc-500 uppercase">+{er} ER</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="border border-zinc-700 p-1.5 mono text-[10px] uppercase tracking-widest text-zinc-400 text-center"
                data-testid="required-units-hidden"
              >
                ??? — DISPATCH A UNIT TO INVESTIGATE
                <div className="mt-0.5 text-[9px] text-zinc-500 normal-case tracking-normal">
                  Requirements will be assessed once first unit is on scene.
                </div>
              </div>
            )}
            <div className="mt-1.5 mono text-[10px] uppercase tracking-widest text-zinc-500">
              State: <span className={
                call.state === "on_scene"
                  ? (allMet ? "text-emerald-400" : "text-orange-400")
                  : "text-yellow-400"
              }>
                {call.state === "on_scene"
                  ? (allMet ? "WORKING SCENE" : "⏸ PAUSED — UNITS NEEDED")
                  : call.revealed
                  ? (allMet ? "MET" : "AWAITING UNITS")
                  : "INVESTIGATING"}
              </span>
            </div>
            {call.state === "on_scene" && call.onSceneStartedAt && (
              <OnSceneCountdown call={call} paused={!allMet} />
            )}
            {/* Patient list — appears once the first unit arrives and patients
                are generated. Folded inside the Required Units box so it
                doesn't dominate the dialog. */}
            {Array.isArray(call.patients) && call.patients.length > 0 && (
              <PatientList call={call} dispatches={dispatches} hospitals={hospitals} onOpenTransport={onOpenTransport} />
            )}
            {/* Arrestees — police calls only. Rendered as compact rows
                similar to the patient list. Auto-detained after ~30 s of
                Patrol / Deputy presence. */}
            {Array.isArray(call.arrestees) && call.arrestees.length > 0 && (
              <div className="border border-blue-500/30 bg-blue-500/[0.04] mt-2" data-testid="arrestee-list">
                <div className="px-2 py-0.5 mono text-[9px] uppercase tracking-widest text-blue-300 border-b border-blue-500/20">
                  Arrestees ({call.arrestees.length})
                </div>
                {call.arrestees.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`arrestee-${a.id}`}
                    className="grid grid-cols-12 items-center px-2 py-0.5 gap-1 text-[11px]"
                  >
                    <span className="col-span-5 truncate text-zinc-300">Suspect · {a.id.slice(-4)}</span>
                    <span className="col-span-4 text-right">
                      {a.status === "detained" ? (
                        <span className="mono text-[10px] uppercase tracking-widest text-emerald-300">DETAINED</span>
                      ) : a.status === "in_transit" ? (
                        <span className="mono text-[10px] uppercase tracking-widest text-amber-300">EN ROUTE</span>
                      ) : a.status === "transported" ? (
                        <span className="mono text-[10px] uppercase tracking-widest text-cyan-300">IN CUSTODY</span>
                      ) : (
                        <span className="mono text-[10px] uppercase tracking-widest text-amber-300">PENDING</span>
                      )}
                    </span>
                    <span className="col-span-3 text-right">
                      {a.status === "detained" && onTransportArrestee && (
                        <button
                          type="button"
                          data-testid={`transport-arrestee-${a.id}`}
                          onClick={() => onTransportArrestee(call.id, a.id)}
                          className="mono text-[9px] uppercase tracking-widest border border-blue-400/50 text-blue-300 hover:bg-blue-400/10 px-1.5 py-px"
                          title="Transport to nearest police station with a free jail cell"
                        >
                          TX
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* En-route + on-scene units for this call + queued Follow-Ups */}
        <DispatchedList
          call={call}
          dispatches={dispatches}
          stations={stations}
          onCancel={onCancelDispatch}
          followUpQueue={followUpQueue}
          apparatusState={apparatusState}
          onToggleFollowUp={onToggleFollowUp}
        />
        </div>{/* /top-scroll */}

        {/* Compact filter bar — single row: type filter chips (left, grow to
            fill) + list-mode + sort options (right, small). This layout
            leaves horizontal room for many more unit types over time. */}
        <div className="border border-white/10 px-2 py-1.5 mt-3 shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="flex flex-wrap gap-1 flex-1 min-w-0">
              <button
                data-testid="filter-type-all"
                onClick={() => setTypeFilter("all")}
                className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                  typeFilter === "all" ? "border-white text-white" : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                ALL
              </button>
              {UNIT_TYPES.map((u) => (
                <button
                  key={u}
                  data-testid={`filter-type-${u}`}
                  onClick={() => setTypeFilter(u)}
                  className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                    typeFilter === u ? "" : "border-white/10 text-zinc-500 hover:text-white"
                  }`}
                  style={typeFilter === u ? { borderColor: UNIT_COLOR[u], color: UNIT_COLOR[u] } : {}}
                >
                  {UNIT_SHORT[u]}
                </button>
              ))}
            </div>
            {/* Right cluster: Follow-Up toggle + Sort pills. Small enough
                to leave the type filters lots of room to grow. */}
            <div className="flex items-center gap-1 shrink-0 pl-1 border-l border-white/10 ml-1">
              <button
                data-testid="list-mode-followup"
                onClick={() => setListMode((m) => (m === "followup" ? "available" : "followup"))}
                title="Show units currently on OTHER calls; queue them to auto-respond here once they clear."
                className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                  listMode === "followup"
                    ? "border-cyan-400 text-cyan-300 bg-cyan-400/10"
                    : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                Follow-Up
              </button>
              <button
                data-testid="sort-eta"
                onClick={() => setSortBy("eta")}
                title="Sort by ETA"
                className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                  sortBy === "eta" ? "border-emerald-400 text-emerald-400" : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                ETA↑
              </button>
              <button
                data-testid="sort-type"
                onClick={() => setSortBy("type")}
                title="Sort by unit type A→Z"
                className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                  sortBy === "type" ? "border-emerald-400 text-emerald-400" : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                A-Z
              </button>
            </div>
          </div>
        </div>

        {/* Units list — takes ALL remaining vertical space so switching
            filters or list modes NEVER changes the dialog's outer height.
            Only its internal scroll position adjusts. */}
        <div
          className="border border-white/10 mt-2 flex-1 min-h-0 overflow-y-auto"
          data-testid={listMode === "followup" ? "followup-units" : "available-units"}
        >
          <div className="grid grid-cols-12 px-2 py-1 mono text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/5 sticky top-0 bg-[#0F1115] z-10">
            <span className="col-span-2">TYPE</span>
            <span className="col-span-4">UNIT</span>
            <span className="col-span-3">STATION</span>
            <span className="col-span-1 text-right">ETA</span>
            <span className="col-span-2 text-right">ACTION</span>
          </div>
          {listMode === "available" ? (
            available.length === 0 ? (
              <div className="p-3 text-center mono text-[11px] text-zinc-500 uppercase">
                No matching available units
              </div>
            ) : available.map((u) => (
              <div
                key={u.id}
                className="grid grid-cols-12 items-center px-2 py-0.5 border-b border-white/5 hover:bg-white/5 gap-1 leading-tight"
              >
                <span className="col-span-2">
                  <span
                    className="mono text-[9px] uppercase tracking-widest px-1 py-px"
                    style={{ background: UNIT_COLOR[u.type], color: "#050505" }}
                  >
                    {UNIT_SHORT[u.type]}
                  </span>
                </span>
                <span className="col-span-4 text-[11px] truncate" title={u.name}>{u.name}</span>
                <span className="col-span-3 mono text-[9px] text-zinc-400 uppercase truncate" title={u.stationKind === "hospital" ? `${u.stationName} (hospital)` : u.stationName}>
                  {u.stationKind === "hospital" && (
                    <span className="text-pink-300 mr-0.5" title="Stationed at hospital">⚕</span>
                  )}
                  {u.stationName}
                  {u.returning && (
                    <span className="ml-1 text-cyan-400" title="Currently mid-return — ETA reflects unit's live position">· RET</span>
                  )}
                </span>
                <span className="col-span-1 text-right mono text-[10px] text-white">
                  {u.isRouting && !u.isRouted ? (
                    <span className="text-zinc-500" title="Calculating road route…">…</span>
                  ) : (
                    <>
                      {formatEta(u.etaSec)}
                      {u.isRouted && (
                        <span className="ml-1 text-emerald-400" title="ETA via real road route (OSRM)">✓</span>
                      )}
                    </>
                  )}
                </span>
                <span className="col-span-2 text-right">
                  <button
                    type="button"
                    data-testid={`dispatch-unit-${u.id}`}
                    onClick={() => onDispatch(call.id, u.id)}
                    className="mono text-[9px] uppercase tracking-widest border border-emerald-500 text-black bg-emerald-400 hover:bg-emerald-300 px-1.5 py-px transition-colors"
                  >
                    DISPATCH
                  </button>
                </span>
              </div>
            ))
          ) : (
            /* Follow-Up mode — busy units currently on other calls. */
            followUpCandidates.length === 0 ? (
              <div className="p-3 text-center mono text-[11px] text-zinc-500 uppercase">
                No busy units to queue
                <div className="text-[9px] text-zinc-600 normal-case tracking-normal mt-1">
                  Follow-Up shows units on OTHER active calls so you can pre-send them here.
                </div>
              </div>
            ) : followUpCandidates.map((u) => (
              <div
                key={u.id}
                data-testid={`followup-row-${u.id}`}
                className={`grid grid-cols-12 items-center px-2 py-0.5 border-b border-white/5 gap-1 leading-tight ${
                  u.queuedHere ? "bg-cyan-500/10" : u.queuedElsewhere ? "opacity-60" : "hover:bg-white/5"
                }`}
              >
                <span className="col-span-2">
                  <span
                    className="mono text-[9px] uppercase tracking-widest px-1 py-px"
                    style={{ background: UNIT_COLOR[u.type], color: "#050505" }}
                  >
                    {UNIT_SHORT[u.type]}
                  </span>
                </span>
                <span className="col-span-4 text-[11px] truncate" title={u.name}>{u.name}</span>
                <span className="col-span-3 mono text-[9px] text-zinc-400 uppercase truncate" title={u.stationName}>
                  {u.stationKind === "hospital" && (
                    <span className="text-pink-300 mr-0.5">⚕</span>
                  )}
                  {u.stationName}
                  <span className="ml-1 text-orange-300" title={`Clears in ~${u.clearInSec}s`}>· BUSY {formatEta(u.clearInSec)}</span>
                </span>
                <span className="col-span-1 text-right mono text-[10px] text-white">
                  {formatEta(u.etaSec)}
                </span>
                <span className="col-span-2 text-right">
                  {u.queuedElsewhere ? (
                    <button
                      type="button"
                      data-testid={`followup-toggle-${u.id}`}
                      onClick={() => onToggleFollowUp && onToggleFollowUp(call.id, u.id)}
                      title="Currently queued for another call — click to move it to THIS call"
                      className="mono text-[9px] uppercase tracking-widest border border-amber-400 text-amber-300 hover:bg-amber-400/10 px-1.5 py-px transition-colors"
                    >
                      MOVE HERE
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`followup-toggle-${u.id}`}
                      onClick={() => onToggleFollowUp && onToggleFollowUp(call.id, u.id)}
                      className={`mono text-[9px] uppercase tracking-widest border px-1.5 py-px transition-colors ${
                        u.queuedHere
                          ? "border-cyan-400 text-cyan-300 hover:bg-cyan-400/10"
                          : "border-cyan-500 text-black bg-cyan-400 hover:bg-cyan-300"
                      }`}
                    >
                      {u.queuedHere ? "QUEUED ✓" : "QUEUE"}
                    </button>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
        </div>{/* /middle flex-col */}

        <DialogFooter className="border-t border-white/10 pt-3 mt-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none mono uppercase text-[11px] tracking-widest text-zinc-400 hover:text-white hover:bg-white/5"
            data-testid="call-dialog-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnSceneCountdown({ call, paused = false }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, []);
  const elapsed = (Date.now() - call.onSceneStartedAt) / 1000;
  const remaining = Math.max(0, Math.ceil(call.onSceneDurationSec - elapsed));
  const pct = Math.max(0, Math.min(100, 100 - (elapsed / call.onSceneDurationSec) * 100));
  return (
    <div className={`mt-3 border p-2 ${paused ? "border-orange-500/50" : "border-emerald-500/40"}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
          Mission Timer {paused && <span className="text-orange-400">— ⏸ PAUSED</span>}
        </span>
        <span className={`mono text-base font-bold ${paused ? "text-orange-400" : "text-emerald-400"}`}>
          {remaining}s <span className="text-[10px] text-zinc-500">/ {call.onSceneDurationSec}s</span>
        </span>
      </div>
      <div className="h-1 bg-white/5 overflow-hidden">
        <div
          className={`h-full ${paused ? "bg-orange-400/70" : "bg-emerald-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DispatchedList({ call, dispatches, stations, onCancel, followUpQueue = {}, apparatusState = {}, onToggleFollowUp }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, []);

  const rows = dispatches
    .filter((d) => d.callId === call.id && !d.returning)
    .map((d) => {
      const station = stations.find((s) => s.id === d.stationId);
      const elapsed = (Date.now() - d.startedAt) / 1000;
      const remaining = Math.max(0, Math.ceil(d.durationSec - elapsed));
      // If the dispatch is in the 3s stagger queue, surface a separate
      // "queueRemaining" countdown so the UI can show "QUEUED Xs" before
      // the unit starts physically responding.
      const queueRemaining = d.queued && d.queuedUntil
        ? Math.max(0, Math.ceil((d.queuedUntil - Date.now()) / 1000))
        : 0;
      return { d, station, remaining, queueRemaining };
    });

  // Queued Follow-Up units for this call — currently busy on other calls
  // and pre-assigned to auto-dispatch here once they clear. Rendered as
  // extra rows below the active dispatch list with a distinct FOLLOW-UP
  // pill and dashed row border so the dispatcher sees the full pipeline
  // of incoming resources at a glance.
  const queuedIds = followUpQueue[String(call.id)] || [];
  const queuedRows = queuedIds
    .map((aid) => {
      const info = apparatusState?.[aid];
      if (!info) return null;
      const station = stations.find((s) => s.id === info.stationId);
      return { aid, info, station };
    })
    .filter(Boolean);

  if (rows.length === 0 && queuedRows.length === 0) return null;

  const fmt = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);

  return (
    <div className="border border-white/10 mt-2 overflow-y-auto" data-testid="dispatched-list">
      <div className="px-2 py-0.5 mono text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/5 flex justify-between sticky top-0 bg-[#0F1115]">
        <span>Dispatched ({rows.length}{queuedRows.length > 0 ? ` + ${queuedRows.length} FU` : ""})</span>
        <span>ETA · Cancel</span>
      </div>
      {rows.map(({ d, station, remaining, queueRemaining }) => (
        <div
          key={d.id}
          data-testid={`dispatched-${d.id}`}
          className="grid grid-cols-12 items-center px-2 py-0.5 border-b border-white/5 last:border-b-0 gap-1 leading-tight"
        >
          <span className="col-span-2">
            <span
              className="mono text-[9px] uppercase tracking-widest px-1 py-px"
              style={{ background: UNIT_COLOR[d.apparatusType] || "#444", color: "#050505" }}
            >
              {UNIT_SHORT[d.apparatusType] || "ENG"}
            </span>
          </span>
          <span className="col-span-4 text-[11px] truncate" title={d.apparatusName}>
            {d.apparatusName}
          </span>
          <span className="col-span-3 mono text-[9px] text-zinc-400 uppercase truncate" title={station?.name}>
            {station?.name || "—"}
          </span>
          <span className="col-span-1 text-right">
            {d.queued ? (
              <span
                className="mono text-[10px] font-bold text-cyan-300"
                title={`Staggered dispatch — responds in ${queueRemaining}s`}
              >
                Q{queueRemaining}s
              </span>
            ) : d.onScene ? (
              <span className="mono text-[9px] uppercase tracking-widest text-emerald-400">ON</span>
            ) : (
              <span className="mono text-[10px] font-bold text-yellow-400">{fmt(remaining)}</span>
            )}
          </span>
          <span className="col-span-2 text-right">
            {d.external ? (
              <span className="mono text-[9px] uppercase tracking-widest text-zinc-500" title="Another player's unit">
                ALLY
              </span>
            ) : (
              <button
                data-testid={`cancel-dispatch-${d.id}`}
                onClick={() => onCancel && onCancel(d.id)}
                className="mono text-[9px] uppercase tracking-widest border border-red-500/70 text-red-400 hover:bg-red-500 hover:text-black px-1.5 py-px transition-colors"
                title="Recall this unit to its station"
              >
                CANCEL
              </button>
            )}
          </span>
        </div>
      ))}
      {/* Queued Follow-Up rows — visually distinct (dashed border, muted
          text, FOLLOW-UP status pill) so the dispatcher can tell at a
          glance which units are still stuck on another call. */}
      {queuedRows.map(({ aid, info, station }) => (
        <div
          key={`fu-${aid}`}
          data-testid={`followup-queued-${aid}`}
          className="grid grid-cols-12 items-center px-2 py-0.5 border-b border-dashed border-white/10 last:border-b-0 gap-1 leading-tight bg-white/[0.02]"
        >
          <span className="col-span-2">
            <span
              className="mono text-[9px] uppercase tracking-widest px-1 py-px"
              style={{ background: UNIT_COLOR[info.type] || "#444", color: "#050505" }}
            >
              {UNIT_SHORT[info.type] || "ENG"}
            </span>
          </span>
          <span className="col-span-4 text-[11px] truncate text-zinc-300" title={info.name}>
            {info.name}
          </span>
          <span className="col-span-3 mono text-[9px] text-zinc-500 uppercase truncate" title={station?.name}>
            {station?.name || "—"}
          </span>
          <span className="col-span-1 text-right">
            <span
              className="mono text-[9px] uppercase tracking-widest text-cyan-300"
              title="Queued — will auto-dispatch when this unit finishes its current call"
            >
              FU
            </span>
          </span>
          <span className="col-span-2 text-right">
            <button
              data-testid={`unqueue-followup-${aid}`}
              onClick={() => onToggleFollowUp && onToggleFollowUp(call.id, aid)}
              className="mono text-[9px] uppercase tracking-widest border border-white/20 text-zinc-400 hover:bg-white/10 hover:text-white px-1.5 py-px transition-colors"
              title="Remove from the follow-up queue"
            >
              UNQUEUE
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}



// ---------- Patient List (Injury Accident calls) ----------
function PatientList({ call, dispatches, hospitals, onOpenTransport }) {
  // Determine if there's at least one Medic on scene to indicate to the
  // user that assessment will progress. Pulled from `call.patients` itself
  // — the engine writes to it; we don't need apparatus state here.
  const patients = call.patients || [];
  const assessSecs = EMERGENCY_TYPES[call.type]?.assess_seconds || PATIENT_ASSESS_SECONDS;
  // Count free transport carriers split by type. HEMS-required patients
  // can ONLY consume HEMS slots; others prefer Medic.
  const onSceneMH = (dispatches || []).filter(
    (d) =>
      d.callId === call.id
      && d.onScene
      && !d.transporting
      && !d.returning
      && (d.apparatusType === "Medic" || d.apparatusType === "HEMS"),
  );
  let remainingHemsSlots = onSceneMH.filter((d) => d.apparatusType === "HEMS").length;
  let remainingMedicSlots = onSceneMH.filter((d) => d.apparatusType === "Medic").length;
  return (
    <div className="mt-1.5 pt-1.5 border-t border-pink-500/20" data-testid="patient-list">
      <div className="mono text-[9px] uppercase tracking-widest text-pink-300 mb-1 flex items-center gap-1">
        <Heartbeat size={10} weight="fill" /> Patients ({patients.length})
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 gap-y-0.5">
        {patients.map((p, i) => {
          const pct = Math.round(((p.assessProgress || 0) * 100));
          const delivered = p.transportStatus === "delivered";
          const inFlight = p.transportStatus === "in_transit" || p.transportStatus === "queued";
          // Backwards compat: pre-existing patients (saved before names were
          // added) fall back to the old PT-N label so old saves still work.
          const label = p.name || `PT${i + 1}`;
          // Find the carrier dispatch + destination hospital for this patient.
          // Used by the "Dest" hover tooltip + the delivered toast text.
          const carrier = (dispatches || []).find((x) => x.transportPatientId === p.id) || null;
          const destHospitalName =
            carrier?.transportHospitalName
            || (hospitals || []).find((h) => h.id === (p.destinationHospitalId || carrier?.transportHospitalId))?.name
            || p.deliveredHospital
            || null;
          const destTooltip = (() => {
            const unitLine = carrier ? `Unit: ${carrier.apparatusName} (${carrier.apparatusType})` : "Unit: pending";
            const hospLine = destHospitalName ? `Hospital: ${destHospitalName}` : "Hospital: pending";
            return `${unitLine}\n${hospLine}`;
          })();
          const showDestTag = inFlight || delivered;
          return (
            <div
              key={p.id}
              className="flex items-center gap-1.5 mono text-[9px] tracking-wider border border-pink-500/10 bg-pink-500/[0.03] px-1.5 py-1"
              data-testid={`patient-row-${i}`}
            >
              <span className="text-pink-400 text-[8px] shrink-0">●</span>
              <span
                className="text-zinc-200 truncate shrink-0 max-w-[80px]"
                title={label}
              >
                {label}
              </span>
              {showDestTag && (
                <span
                  data-testid={`patient-dest-tag-${i}`}
                  title={destTooltip}
                  className="mono text-[8px] uppercase tracking-widest px-1 border border-cyan-400/40 text-cyan-300 bg-cyan-500/5 shrink-0 cursor-help"
                >
                  Dest
                </span>
              )}
              {delivered ? (
                <span className="text-emerald-400 flex-1 truncate text-right" title={p.deliveredHospital || ""}>
                  Delivered
                </span>
              ) : !p.assessed ? (
                <>
                  <div className="flex-1 h-1 bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-pink-400"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-zinc-400 w-8 text-right shrink-0">{pct}%</span>
                </>
              ) : !p.needsTransport ? (
                <span className="text-emerald-400 flex-1 text-right">Refusal +$250</span>
              ) : (() => {
                // Patient is assessed AND needs transport. Three states:
                //  - critical (requires_hems): only HEMS slot consumes
                //  - regular needs-transport: Medic first, HEMS fallback
                //  - no free carrier for this patient: WAITING badge
                const requiresHems = !!p.requires_hems;
                let hasSlot = false;
                if (!inFlight) {
                  if (requiresHems) {
                    if (remainingHemsSlots > 0) { remainingHemsSlots -= 1; hasSlot = true; }
                  } else if (remainingMedicSlots > 0) {
                    remainingMedicSlots -= 1;
                    hasSlot = true;
                  } else if (remainingHemsSlots > 0) {
                    remainingHemsSlots -= 1;
                    hasSlot = true;
                  }
                }
                return (
                  <>
                    <span className="flex-1 flex items-center justify-end gap-1 truncate">
                      {requiresHems && (
                        <span
                          className="mono text-[8px] uppercase tracking-widest px-1 border border-red-500/50 text-red-300 bg-red-500/10 shrink-0"
                          title="Critical — only a HEMS helicopter can transport this patient"
                          data-testid={`patient-hems-req-${i}`}
                        >
                          HEMS
                        </span>
                      )}
                      {p.specialty ? (
                        <span
                          className="text-amber-300 inline-flex items-center gap-0.5 truncate"
                          title="Specialty tag"
                        >
                          <Star size={8} weight="fill" /> {p.specialty}
                        </span>
                      ) : !requiresHems ? (
                        <span className="text-zinc-400">Std</span>
                      ) : null}
                    </span>
                    {hasSlot || inFlight ? (
                      <button
                        type="button"
                        data-testid={`transport-btn-${i}`}
                        disabled={inFlight}
                        onClick={() => onOpenTransport && onOpenTransport(p, call)}
                        className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-pink-500/60 text-pink-300 hover:bg-pink-500/10 disabled:opacity-30 shrink-0"
                      >
                        {inFlight ? "..." : "Transport"}
                      </button>
                    ) : (
                      <span
                        data-testid={`patient-waiting-tag-${i}`}
                        title={requiresHems
                          ? "Critical patient — needs HEMS transport. No HEMS free on scene."
                          : "Patient is assessed and needs transport but no Medic / HEMS is free on scene. Dispatch another carrier."}
                        className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-400/40 text-amber-300 bg-amber-500/10 shrink-0 cursor-help animate-pulse"
                      >
                        Waiting
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          );
        })}
      </div>
      <div className="mt-1 mono text-[8px] uppercase tracking-widest text-zinc-600">
        Medic on scene · {assessSecs}s assess
      </div>
    </div>
  );
}
